import axios, { AxiosInstance, AxiosHeaders, AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import { getEnvBaseURL, isEnvTest } from '@/utils/env'
import { formatAxiosError } from '@/utils/sensors-helper'

export type ApiResponse<T> = {
  success: boolean
  rtn_msg: string
  data: T
}

/**
 * 是否是网络错误，包含前端和后端
 * @param {*} err
 * @returns
 */
function isNetworkError(err: Error) {
  return isFeNetworkError(err)
}

/**
 * 是否是前端网络错误，
 * @param {*} err
 * @returns
 */
function isFeNetworkError(err: Error) {
  const message = err.message
  return message === 'Network Error' || message.startsWith('timeout ')
}

interface AxiosConfigAdv<T = any> extends AxiosRequestConfig<T> {
  /**
   * 最大重试次数
   */
  retry?: number
  /**
   * 当前重试次数
   */
  retryCount?: number
  /**
   * 重试间隔
   */
  retryInterval?: number
}

interface AxiosInstanceAdv extends AxiosInstance {
  /**
   * 重试间隔
   */
  retryInterval: number
  /**
   * 设备信息，用于接口认证
   */
  clientInfoStr: string
  /**
   * 错误记录器
   */
  errorLogger: CallableFunction

  post<T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: AxiosConfigAdv<D>): Promise<T>
}

const API = axios.create({
  baseURL: getEnvBaseURL(),
  timeout: (isEnvTest() ? 5 : 20) * 1000 // 超时时间
}) as AxiosInstanceAdv

API.retryInterval = 500
API.clientInfoStr = ''
API.errorLogger = null

const logAxiosError = (error) => {
  if (API.errorLogger) {
    const eventParams = formatAxiosError(error)
    API.errorLogger(eventParams)
  }
}

API.interceptors.request.use((config) => {
  if (config.headers instanceof AxiosHeaders) {
    config.headers.set('clientInfo', API.clientInfoStr)
  } else {
    Object.assign(config.headers, {
      clientInfo: API.clientInfoStr
    })
  }
  return config
})

export function isApiSuccess(res: any) {
  return res && (res.rtn_code === '0000' || res.success)
}

//请求后返回数据拦截
API.interceptors.response.use(
  (res) => {
    if (res.status === 200) {
      const data = res.data
      if (!isApiSuccess(data)) {
        logAxiosError(res)
      } else {
        data.success = true
      }
      return data
    } else {
      return res.data
    }
  },
  (res: AxiosError) => {
    const config = res.config as AxiosConfigAdv
    // 如果配置不存在或重试属性未设置，抛出promise错误
    if (!config || !config.retry) {
      return Promise.reject(res)
    }
    // 将数据重新转为对象格式
    if (config.data && typeof config.data === 'string' && config.data[0] == '{') {
      try {
        config.data = JSON.parse(config.data)
      } catch (err) {
        console.log('解析数据出错')
      }
    }
    // 设置一个变量记录重新请求的次数
    if (config.retry > 0) {
      config.retryCount = config.retryCount || 0
      const _isNetworkError = isNetworkError(res)
      // 检查重新请求的次数是否超过我们设定的请求次数
      if (config.retryCount >= config.retry || !_isNetworkError) {
        logAxiosError(res)
        return Promise.reject(res)
      }
      // 重新请求的次数自增
      config.retryCount++
      // 创建新的Promise来处理重新请求的间隙
      const back = new Promise(function (resolve) {
        setTimeout(function () {
          resolve(true)
        }, config.retryInterval || 1)
      })
      //返回axios的实体，重试请求
      return back.then(function () {
        return API(config)
      })
    } else {
      return Promise.reject(res)
    }
  }
)

export default API
